import { useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { apiClient, API } from "@/lib/api";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { Mail, Lock, User, Loader2, ArrowRight } from "lucide-react";

const SignupPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  // Part 2: legal consent. Single checkbox covers age (18+), Terms, and
  // Privacy. Unchecked by default. The form is gated three ways: (a)
  // submit button disabled, (b) handleSubmit re-checks before POST, (c)
  // backend rejects requests with `legal_accepted: false`.
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [searchParams] = useSearchParams();

  const refCode = useMemo(() => searchParams.get("ref") || "", [searchParams]);
  const utmSource = useMemo(() => searchParams.get("utm_source") || "", [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!legalAccepted) {
      toast.error("Please confirm you are 18+ and agree to the Terms & Privacy Policy.");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        legal_accepted: true,
        legal_version: "v1.0",
        terms_version: "v1.0",
        privacy_version: "v1.0",
      };
      if (refCode) payload.referral_code = refCode;
      const response = await apiClient.post('/auth/register', payload);
      login(response.data);

      // Track signup source
      const utm = {};
      ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
        const v = searchParams.get(k);
        if (v) utm[k] = v;
      });
      if (refCode) utm.ref = refCode;
      if (Object.keys(utm).length > 0) {
        fetch(`${API}/behavior/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event: "signup_source", meta: utm }),
          keepalive: true,
        }).catch(() => {});
      }

      toast.success("Account created successfully!");

      // If opened from embedded preview, notify parent and prompt return
      if (window.opener) {
        try { window.opener.postMessage({ type: "vora_auth_success" }, window.location.origin); } catch {}
        return;
      }

      navigate("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="vora-auth-page" data-testid="signup-page">
      <div className="vora-auth-grid" />

      {/* Top nav */}
      <header className="vora-auth-header">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-link">
          <VoraLogo variant="icon" size={22} />
          <span className="text-base font-bold font-heading tracking-tight" style={{ color: "#111111" }}>VORA</span>
        </Link>
        <Link
          to="/login"
          className="text-sm font-medium transition-colors"
          style={{ color: "#6B7280" }}
          data-testid="header-login-link"
          onMouseEnter={e => e.currentTarget.style.color = "#111111"}
          onMouseLeave={e => e.currentTarget.style.color = "#6B7280"}
        >
          Log in
        </Link>
      </header>

      {/* Center card */}
      <main className="vora-auth-main">
        <div className="vora-auth-card" data-testid="signup-card">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold font-heading mb-2" style={{ color: "#111111" }} data-testid="signup-heading">Create your account</h1>
            <p className="text-sm" style={{ color: "#5F6368" }} data-testid="signup-sub">VORA AI builds plans around your body and goals</p>
            {refCode && (
              <p className="text-xs mt-2" style={{ color: "#4E7A67" }} data-testid="signup-referral-badge">
                Invited by a friend
              </p>
            )}
          </div>

          {/* Google */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="google-signup-btn"
            className="vora-auth-google-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="vora-auth-divider">
            <div className="vora-auth-divider-line" />
            <span className="vora-auth-divider-text">or</span>
            <div className="vora-auth-divider-line" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="vora-auth-label">Full Name</label>
              <div className="relative">
                <User className="vora-auth-input-icon" />
                <input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="name-input"
                  className="vora-auth-input"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="vora-auth-label">Email</label>
              <div className="relative">
                <Mail className="vora-auth-input-icon" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="email-input"
                  className="vora-auth-input"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="vora-auth-label">Password</label>
              <div className="relative">
                <Lock className="vora-auth-input-icon" />
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="password-input"
                  className="vora-auth-input"
                  required
                  minLength={6}
                />
              </div>
              <p className="text-xs mt-1.5" style={{ color: "#8B9096" }}>At least 6 characters</p>
            </div>

            {/* Part 2: Legal consent checkbox. Required, unchecked by default,
                and reinforced by handleSubmit + backend re-check. */}
            <label className="vora-auth-consent" data-testid="legal-consent-row">
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
                data-testid="legal-consent-checkbox"
              />
              <span className="vora-auth-consent-label">
                I am at least 18 years old and agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="terms-link">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="privacy-link">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !legalAccepted}
              data-testid="signup-submit-btn"
              className="vora-auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Create Account <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </button>
          </form>

          <p className="text-center text-sm mt-6" style={{ color: "#5F6368" }}>
            Already have an account?{" "}
            <Link to="/login" className="font-medium hover:underline" style={{ color: "#4E7A67" }} data-testid="login-link">
              Log in
            </Link>
          </p>
        </div>

        {utmSource && (
          <p className="text-center text-xs mt-8" style={{ color: "#8B9096" }} data-testid="signup-utm-source">
            via {utmSource}
          </p>
        )}
      </main>
    </div>
  );
};

export default SignupPage;
